import { Component, computed, input } from '@angular/core';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';

/**
 * Testo dei termini e condizioni, in un unico posto.
 *
 * Esiste come componente perché il testo va mostrato in tre contesti — schermata di accettazione,
 * pagina di consultazione e (via link) registrazione — e tenerne più copie garantirebbe che prima
 * o poi divergano.
 *
 * Il contenuto è l'HTML gestito dal SUPERADMIN e restituito dal backend: si rende con `[innerHTML]`,
 * quindi passa dalla sanitizzazione di Angular (oltre a quella già fatta dal backend al salvataggio).
 * Nessun testo di documento vive più nei dizionari.
 *
 * Accessibilità: la pagina che ospita il componente ha già il proprio `<h1>`. Le intestazioni del
 * documento vengono normalizzate in modo che la più alta presente sia h2, senza salti di livello.
 */
@Component({
  selector: 'app-termini-testo',
  templateUrl: './termini-testo.component.html',
  styleUrls: ['./termini-testo.component.scss'],
  imports: [TranslocoModule],
  providers: [provideTranslocoScope({ scope: 'termini', alias: 'termini' })],
})
export class TerminiTestoComponent {
  /** Versione da mostrare, quella comunicata dal backend. Assente: la riga non viene resa. */
  readonly versione = input<string | null | undefined>(null);

  /** HTML del documento dal backend. */
  readonly testoHtml = input<string | null | undefined>(null);

  /**
   * Livello minimo delle intestazioni del documento: 2 sotto l'h1 della pagina (default); 3 dove
   * il titolo del documento è già un h2 (modale dei documenti del Comune).
   */
  readonly livelloMinimo = input<number>(2);

  /** HTML con le intestazioni normalizzate (minimo `livelloMinimo`). */
  readonly testoMostrato = computed(() => abbassaIntestazioni(this.testoHtml() ?? '', this.livelloMinimo()));
}

/**
 * Normalizza le intestazioni: il livello minimo presente diventa `livelloMinimo` (default h2, sotto l'h1 della pagina) e gli
 * altri si spostano della stessa quantità, senza saltare livelli. Se il minimo è già h2 il testo
 * resta invariato. Il backend ammette solo h2–h4, ma la funzione regge anche un h1.
 */
export function abbassaIntestazioni(html: string, livelloMinimo = 2): string {
  const livelli = Array.from(html.matchAll(/<h([1-6])(?=[\s>\/])/gi), (m) => Number(m[1]));
  if (livelli.length === 0) {
    return html;
  }
  const delta = livelloMinimo - Math.min(...livelli);
  if (delta === 0) {
    return html;
  }
  return html.replace(/<(\/?)h([1-6])(?=[\s>\/])/gi, (_m, slash: string, n: string) =>
    `<${slash}h${Math.min(Math.max(Number(n) + delta, livelloMinimo), 6)}`,
  );
}
